import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSiteContent } from "../../context/SiteContentContext";
import { Lines, thaiDate } from "../../components/site/siteText";
import { CATEGORIES, apiUrl, getEditionRange, getEditions, getPublished } from "../../services/newspaperApi";
import DateInput from "../../components/site/DateInput";
import { PageNav, PageSummary, usePaging } from "../../components/admin/Pager";
import "./newspaper.css";

// Archive ("ฉบับย้อนหลัง") = a date range the reader can pick; by default the latest 7 days before today
// (yesterday first). Today is shown in the "ฉบับประจำวันนี้" card.
const ARCHIVE_DAYS = 7;
const MAX_RANGE_DAYS = 92; // same limit as the API
const addDays = (iso, n) => new Date(Date.parse(`${iso}T00:00:00Z`) + n * 86400000).toISOString().slice(0, 10);
const latestRange = (today) => ({ from: addDays(today, -ARCHIVE_DAYS), to: addDays(today, -1) });

function NewspaperHome() {
  const { siteContent } = useSiteContent();
  const c = siteContent.newspaperPage || {};
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [items, setItems] = useState([]);
  const [todayEdition, setTodayEdition] = useState(null);
  const [today, setToday] = useState(new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10));
  const [range, setRange] = useState(() => latestRange(new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10)));
  const [shown, setShown] = useState(range); // the range currently listed
  const [archive, setArchive] = useState([]);
  const [archiveMsg, setArchiveMsg] = useState("");
  // rows per page for the results and the archive (owner 28-10-2569: 5, 10, 20, 30, 40, 50)
  const resultPaging = usePaging(items, "mng_news_results_page_size", params.toString());
  const archivePaging = usePaging(archive, "mng_news_archive_page_size", shown.from + shown.to);

  useEffect(() => {
    document.title = "หนังสือพิมพ์และประกาศรายวัน | Maneenopkao Group";
    getEditions(1)
      .then((d) => {
        setTodayEdition((d.editions || [])[0] || null);
        if (d.today && d.today !== today) {
          // the server's date (Bangkok) wins — reset the default range to it
          setToday(d.today);
          const r = latestRange(d.today);
          setRange(r);
          setShown(r);
        }
      })
      .catch(() => setTodayEdition(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let active = true;
    getEditionRange(shown.from, shown.to)
      .then((d) => { if (active) { setArchive(d.editions || []); setArchiveMsg(""); } })
      .catch((e) => { if (active) { setArchive([]); setArchiveMsg(e.message || "โหลดฉบับย้อนหลังไม่สำเร็จ"); } });
    return () => { active = false; };
  }, [shown]);

  const showRange = (e) => {
    e.preventDefault();
    if (!range.from || !range.to) return setArchiveMsg("กรุณาเลือกวันที่ให้ครบทั้งสองช่อง");
    const from = range.from <= range.to ? range.from : range.to;
    const to = range.from <= range.to ? range.to : range.from;
    const days = Math.round((Date.parse(to) - Date.parse(from)) / 86400000) + 1;
    if (days > MAX_RANGE_DAYS) return setArchiveMsg(`เลือกช่วงได้ไม่เกิน ${MAX_RANGE_DAYS} วัน`);
    setRange({ from, to });
    setShown({ from, to });
  };
  const showLatest = () => {
    const r = latestRange(today);
    setRange(r);
    setShown(r);
  };

  const [listState, setListState] = useState({ loading: true, error: "" });
  useEffect(() => {
    // Back / Forward changes the URL: show the same search in the boxes as in the results
    setQ(params.get("q") || "");
    setCategory(params.get("category") || "");
    let active = true;
    setListState({ loading: true, error: "" });
    getPublished(params.get("q") || "", params.get("category") || "")
      .then((d) => { if (active) { setItems(d.items || []); setListState({ loading: false, error: "" }); } })
      .catch((e) => { if (active) { setItems([]); setListState({ loading: false, error: e.message || "โหลดรายการไม่สำเร็จ" }); } });
    return () => { active = false; };
  }, [params]);

  const search = (e) => {
    e.preventDefault();
    const next = {};
    if (q.trim()) next.q = q.trim();
    if (category) next.category = category;
    setParams(next);
  };


  return (
    <main id="main">
      <section className="container newspaper-masthead">
        <p className="eyebrow">{c.eyebrow}</p>
        <div className="masthead-row">
          <h1>
            {c.title}
            <span>{c.titleAccent}</span>
          </h1>
          <div>
            <p>{c.subtitle}</p>
            <strong>{thaiDate(today)}</strong>
          </div>
        </div>
        <div className="masthead-strip">
          <span>{c.strip}</span>
          <Link to="/newspaper/submit">{c.stripLinkLabel}</Link>
        </div>
      </section>

      <section className="container newspaper-lead">
        <div>
          <p className="eyebrow">{c.leadEyebrow}</p>
          <h2><Lines text={c.leadTitle} /></h2>
          <p>{c.leadText}</p>
          <Link className="button dark" to="/newspaper/submit">
            {c.leadButton}{" "}
            <span>↗</span>
          </Link>
        </div>
        <aside className="today-edition">
          <span>{c.todayLabel}</span>
          <h3>{c.todayTitle}</h3>
          <p>{thaiDate(today)}</p>
          {todayEdition && todayEdition.count > 0 ? (
            <>
              <strong>
                {todayEdition.issueNo ? `ฉบับที่ ${todayEdition.issueNo} ปี ${todayEdition.issueYear} · ` : ""}{todayEdition.count} รายการ
              </strong>
              <Link className="text-link" to={`/newspaper/editions/${today}`}>
                {c.todayOpenLabel}{" "}
                <span>↗</span>
              </Link>
            </>
          ) : (
            <p className="empty-small"><Lines text={c.todayEmpty} /></p>
          )}
        </aside>
      </section>

      <section className="container newspaper-content">
        <div className="archive-main">
          <div className="archive-heading">
            <h2>{c.listTitle}</h2>
            <span>{c.listCaption}</span>
          </div>
          <form className="archive-search site-form" onSubmit={search}>
            <label>
              <span className="sr-only">ค้นหาประกาศ</span>
              <input name="q" value={q} onChange={(e) => setQ(e.target.value)} placeholder={c.searchPlaceholder} />
            </label>
            <label>
              <span className="sr-only">ประเภทเนื้อหา</span>
              <select name="category" value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">ทุกประเภท</option>
                {CATEGORIES.map((cat) => <option key={cat}>{cat}</option>)}
              </select>
            </label>
            <button className="button dark" type="submit">{c.searchButton}</button>
          </form>

          {listState.loading && items.length === 0 ? (
            <p className="no-notices">กำลังโหลดรายการ…</p>
          ) : listState.error ? (
            <p className="no-notices">โหลดรายการไม่สำเร็จ: {listState.error} · กรุณาลองใหม่อีกครั้ง</p>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <span>↳</span>
              <h3>{c.emptyTitle}</h3>
              <p>{c.emptyText}</p>
              <Link className="text-link" to="/newspaper/submit">
                {c.emptyLink}{" "}
                <span>↗</span>
              </Link>
            </div>
          ) : (
            <>
            <PageSummary paging={resultPaging} />
            <div className="archive-results">
              {resultPaging.shown.map((it) => (
                <Link
                  key={it.id}
                  className={`archive-result${it.kind === "article" && it.files?.[0]?.url ? " has-thumb" : ""}`}
                  to={it.kind === "article" ? `/newspaper/articles/${it.id}` : { pathname: `/newspaper/editions/${it.issueDate}`, hash: `notice-${it.id}` }}
                >
                  <span>{thaiDate(it.issueDate)}</span>
                  <div>
                    <h3>{it.title}</h3>
                    <p>{[it.category, it.companyName].filter(Boolean).join(" · ")}</p>
                  </div>
                  {it.kind === "article" && it.files?.[0]?.url && (
                    <img className="archive-thumb" src={apiUrl(it.files[0].url)} alt="" loading="lazy" />
                  )}
                  <em>↗</em>
                </Link>
              ))}
            </div>
            <PageNav paging={resultPaging} />
            </>
          )}
        </div>

        <aside className="edition-archive">
          <p className="eyebrow">{c.archiveEyebrow}</p>
          <h3>{c.archiveTitle}</h3>
          <form className="archive-range site-form" onSubmit={showRange}>
            <label>
              ตั้งแต่
              <DateInput value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
            </label>
            <label>
              ถึง
              <DateInput value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
            </label>
            <div className="archive-range-actions">
              <button className="button dark" type="submit">ดูช่วงนี้</button>
              <button className="text-link" type="button" onClick={showLatest}>{ARCHIVE_DAYS} วันล่าสุด</button>
            </div>
          </form>
          <p className="archive-range-caption">
            {thaiDate(shown.from)} – {thaiDate(shown.to)} · {archive.length} ฉบับ
          </p>
          {archiveMsg && <p className="archive-range-msg">{archiveMsg}</p>}
          {!archiveMsg && archive.length === 0 && <p className="archive-range-msg">ไม่มีฉบับในช่วงนี้</p>}
          {archive.length > 0 && <PageSummary paging={archivePaging} unit="ฉบับ" />}
          {archivePaging.shown.map((ed) => (
            <Link key={ed.date} to={`/newspaper/editions/${ed.date}`}>
              <span>
                {thaiDate(ed.date)}
                <small>
                  {ed.issueNo ? `ฉบับที่ ${ed.issueNo} ปี ${ed.issueYear} · ` : ""}{ed.count} รายการ
                  {ed.count > 0 && !ed.pdfReady ? ` · PDF ได้ ${thaiDate(ed.downloadFrom)}` : ""}
                </small>
              </span>
              <span>↗</span>
            </Link>
          ))}
          <PageNav paging={archivePaging} />
          <div className="archive-help">
            <h4>{c.helpTitle}</h4>
            <p>{c.helpText}</p>
            <Link className="button dark" to="/newspaper/submit">{c.helpButton}</Link>
          </div>
        </aside>
      </section>
    </main>
  );
}

export default NewspaperHome;
